import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Crown,
  Loader2,
  Mail,
  Phone,
  User,
  Building2,
  ArrowRight,
  MessageSquare,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * QuarterlyLeadDialog — high-ticket lead capture for the Executive
 * Leadership Quarterly plan (₹48,000 / $1,490 · 90 days, fully managed).
 *
 * Reuses the /api/enterprise/enquiries endpoint with plan_kind set to
 * "quarterly_leadership" so sales gets one inbox with both surfaces
 * tagged. Fields: Name, Work Email, Phone, Company, Message. Honeypot
 * (`website`) drops trivial bots server-side.
 */
export const QuarterlyLeadDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({
    name: '',
    work_email: '',
    phone: '',
    company: '',
    message: '',
    website: '', // honeypot
  });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const patch = (k) => (e) => {
    setForm((prev) => ({ ...prev, [k]: e.target.value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const reset = () => {
    setForm({ name: '', work_email: '', phone: '', company: '', message: '', website: '' });
    setErrors({});
    setBusy(false);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim() || form.name.trim().length < 2) next.name = 'Please share your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email.trim()))
      next.work_email = 'Enter a valid work email.';
    if (!form.phone.trim() || form.phone.trim().replace(/\D/g, '').length < 4)
      next.phone = 'Enter a phone number we can reach you on.';
    if (!form.company.trim()) next.company = 'Enter your company name.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/enterprise/enquiries`,
        {
          name: form.name.trim(),
          work_email: form.work_email.trim().toLowerCase(),
          phone: form.phone.trim(),
          company: form.company.trim(),
          message: form.message.trim(),
          plan_kind: 'quarterly_leadership',
          website: form.website || '',
        },
        { timeout: 15000, headers: { 'Content-Type': 'application/json' } },
      );
      if (res?.data?.ok) {
        toast.success("Thanks! A senior strategist will reach out within one business day.");
        reset();
        onOpenChange?.(false);
      } else {
        toast.error('Could not submit right now. Please try again in a moment.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
            ? detail[0].msg
            : 'Could not submit right now. Please try again or email help@blayzrr.com.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange?.(next);
      }}
    >
      <DialogContent
        className="sm:max-w-[560px] rounded-2xl"
        data-testid="quarterly-lead-dialog"
      >
        <DialogHeader>
          <div className="mx-auto sm:mx-0 mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Crown size={18} aria-hidden="true" />
          </div>
          <DialogTitle className="text-xl sm:text-2xl tracking-[-0.02em]">
            Executive Leadership Quarterly
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            90 days, fully managed — strategist, content, benchmarking, connections. Tell us about
            your goals and a senior strategist will reach out within one business day.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="quarterly-lead-form">
          {/* honeypot */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-10000px',
              top: 'auto',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
            }}
          >
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={patch('website')}
              />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FieldRow
              id="quarterly-name"
              label="Name"
              Icon={User}
              placeholder="Jane Doe"
              value={form.name}
              onChange={patch('name')}
              error={errors.name}
              testId="quarterly-name-input"
              autoComplete="name"
              required
            />
            <FieldRow
              id="quarterly-work-email"
              label="Work Email"
              Icon={Mail}
              type="email"
              placeholder="jane@company.com"
              value={form.work_email}
              onChange={patch('work_email')}
              error={errors.work_email}
              testId="quarterly-work-email-input"
              autoComplete="email"
              required
            />
            <FieldRow
              id="quarterly-phone"
              label="Phone"
              Icon={Phone}
              type="tel"
              placeholder="+91 9x xxx xxx"
              value={form.phone}
              onChange={patch('phone')}
              error={errors.phone}
              testId="quarterly-phone-input"
              autoComplete="tel"
              required
            />
            <FieldRow
              id="quarterly-company"
              label="Company"
              Icon={Building2}
              placeholder="Acme Inc."
              value={form.company}
              onChange={patch('company')}
              error={errors.company}
              testId="quarterly-company-input"
              autoComplete="organization"
              required
            />
          </div>

          <div>
            <Label
              htmlFor="quarterly-message"
              className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground"
            >
              What are you hoping to achieve?
            </Label>
            <div className="mt-1.5 relative">
              <MessageSquare
                size={14}
                className="absolute left-3 top-3 text-muted-foreground"
                aria-hidden="true"
              />
              <Textarea
                id="quarterly-message"
                placeholder="Position for a board seat, land C-suite clients, launch a founder-led narrative…"
                value={form.message}
                onChange={patch('message')}
                className="min-h-[96px] rounded-xl pl-9"
                data-testid="quarterly-message-input"
              />
            </div>
          </div>

          <DialogFooter className="mt-6 gap-2 sm:gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={busy}
              data-testid="quarterly-lead-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="rounded-full h-11 px-6"
              data-testid="quarterly-lead-submit"
            >
              {busy ? (
                <>
                  <Loader2 size={15} className="mr-2 animate-spin" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                <>
                  Request a strategy call
                  <ArrowRight size={15} className="ml-2" aria-hidden="true" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const FieldRow = ({
  id,
  label,
  Icon,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  testId,
  autoComplete,
  required,
}) => (
  <div>
    <Label
      htmlFor={id}
      className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground"
    >
      {label}
    </Label>
    <div className="mt-1.5 relative">
      {Icon ? (
        <Icon
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
      ) : null}
      <Input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`h-11 rounded-xl ${Icon ? 'pl-9' : ''} ${error ? 'border-destructive/70 focus-visible:ring-destructive/50' : ''}`}
        data-testid={testId}
        autoComplete={autoComplete}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        required={required}
      />
    </div>
    {error ? (
      <p id={`${id}-error`} className="mt-1.5 text-[12px] text-destructive">
        {error}
      </p>
    ) : null}
  </div>
);

export default QuarterlyLeadDialog;
